import React, { useState, useMemo } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import { FaSearchPlus, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { Download, Search } from "lucide-react";
import * as XLSX from 'xlsx';
//import jsPDF from 'jspdf';

const AdvancedDataTableComponent = ({ headers, data, headerMapping, onEdit, showActions, buttonLableName, checkedItems, onCheckAll, extatAction, extraActionMethod, exportExcel }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  var buttonLabel = 'Edit';
  if (buttonLableName && buttonLableName != '' && buttonLableName != 'undefined') {
    buttonLabel = buttonLableName;
  }

  const columns = useMemo(() => {
    const headerColumns = headers.map(header => ({
      Header: header,
      accessor: headerMapping[header],
    }));

    if (showActions) {
      headerColumns.push({
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => (
          <div>
            <button
              className="bg-blue-500 text-white px-2 py-1 rounded mr-2"
              onClick={() => onEdit(row.original)}
            >
              {buttonLabel}
            </button>
          </div>
        ),
      });
    }
    if (extatAction) {
      // Add a custom Checkin ID column
      headerColumns.push({
        Header: 'Show',
        accessor: 'show',
        isVisible: false, // Make it invisible in the table
        Cell: ({ row }) => (
          <div
            className="text-blue-500 cursor-pointer"
            onClick={() => extraActionMethod(row.original)}
          >
            <FaSearchPlus />
          </div>
        ),
      });
    }
    return headerColumns;
  }, [headers, headerMapping, showActions, onEdit]);


  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(row =>
      headers.some(header =>
        String(row[headerMapping[header]] ?? '').toLowerCase().includes(term)
      )
    );
  }, [searchTerm, data, headers, headerMapping]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: 0, pageSize: recordsPerPage },
    },
    useSortBy,
    usePagination
  );
  const exportToExcel = () => {
    // Prepare headers to be exported (only the ones visible in the table)
    const visibleHeaders = columns.filter(col => col.isVisible !== false).map(col => col.Header);

    // Prepare data for export (only the visible columns)
    const dataToExport = page.map(row => {
      const rowData = {};
      row.cells.forEach(cell => {
        if (cell.column.isVisible !== false) {
          rowData[cell.column.Header] = cell.value;
        }
      });
      return rowData;
    });

    // Create the worksheet
    const worksheet = XLSX.utils.json_to_sheet(dataToExport, { header: visibleHeaders });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

    // Export the file
    XLSX.writeFile(workbook, 'data.xlsx');
  };

  const exportToPDF = () => {
    // const pdf = new jsPDF();
    // pdf.text('Exported Data', 10, 10);
    // const dataStr = JSON.stringify(filteredData, null, 2);
    // pdf.text(dataStr, 10, 20);
    // pdf.save('data.pdf');
  };

  return (
    <div className="p-2 bg-gradient-to-b from-blue-50 via-white to-blue-100 rounded-xl shadow-lg">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label>
            Show{' '}
            <select
              value={recordsPerPage}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setRecordsPerPage(Number(e.target.value));
              }}
              className="border rounded-md p-2 shadow-sm"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>{' '}
            records per page
          </label>
        </div>
        <div className="mb-4"></div>
        <div>
          <input
            type="text"
            placeholder="Search..."
            className="w-full border border-black px-2 py-2 rounded-md shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Responsive table container */}
      <div className="overflow-x-auto shadow-lg rounded-xl border border-gray-200">
        <table {...getTableProps()} className="min-w-full table-auto">
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()} className="bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-md">
                {headerGroup.headers.map(column => {
                  return (
                    <th key={column.id} {...column.getHeaderProps(column.getSortByToggleProps())} className="px-6 py-3 text-xs md:text-sm font-semibold">
                      {column.id === 'check' && (
                        <input
                          type="checkbox"
                          className="checkbox-box w-5 h-5 border-2 border-gray-300 rounded-full bg-white bg-opacity-20 backdrop-blur-md checked:bg-blue-500 checked:border-blue-500 focus:ring-0 transition-colors duration-300 ease-in-out transform scale-100 hover:scale-105 shadow-sm hover:shadow-md"
                          checked={checkedItems.length === data.length}
                          onChange={onCheckAll}
                        />
                      )}
                      {column.render('Header')}

                      <span>
                        {column.isSorted
                          ? column.isSortedDesc
                            ? ' 🔽'
                            : ' 🔼'
                          : ''}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, index) => {
              prepareRow(row);
              return (
                <tr key={row.id} {...row.getRowProps()} className={`transition-all duration-300 ease-in-out 
                  ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} 
                  hover:bg-blue-50 hover:shadow-lg`}>
                  {row.cells.map(cell => (
                    <td key={cell.column.id} {...cell.getCellProps()} className="border px-4 py-2 text-xs md:text-sm">
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Export buttons and pagination controls */}
      <div className="flex justify-between items-center mt-4">
        
        <div>
          {/* <button
            className="bg-green-500 text-white px-3 py-2 mr-2 rounded"
            onClick={exportToExcel}
          >
            Export to Excel
          </button> */}
          {exportExcel && (
          <button onClick={exportToExcel} className="bg-green-500 text-white px-3 py-2 mr-2 rounded">
            <Download size={20} />
          </button>
          )}
          {/* <button
            className="bg-red-500 text-white px-3 py-2 rounded"
            onClick={exportToPDF}
          >
            Export to PDF
          </button> */}
        </div>
        
        {/* Pagination controls */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 disabled:opacity-50"
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              <FaArrowLeft size={18} /> {/* Left arrow for previous */}
            </button>
            <span className="text-lg">
              Page {pageIndex + 1} of {pageOptions.length}
            </span>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 disabled:opacity-50"
              onClick={() => nextPage()}
              disabled={!canNextPage}
            >
              <FaArrowRight size={18} /> {/* Right arrow for next */}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedDataTableComponent;
